//
// Imports
//

import * as BrowserUtilities from "@donutteam/browser-utilities";
import { autoUpdate, computePosition, flip, Placement, shift, limitShift } from "@floating-ui/dom";
import { z } from "zod";

//
// Locals
//

const PlacementSchema = z.enum(
	[
		"top" satisfies Placement,
		"top-start" satisfies Placement,
		"top-end" satisfies Placement,

		"right" satisfies Placement,
		"right-start" satisfies Placement,
		"right-end" satisfies Placement,

		"bottom" satisfies Placement,
		"bottom-start" satisfies Placement,
		"bottom-end" satisfies Placement,

		"left" satisfies Placement,
		"left-start" satisfies Placement,
		"left-end" satisfies Placement,
	]);

function closeAllPopups()
{
	const popupElements = document.querySelectorAll<HTMLElement>(".component-popup");

	for (const popupElement of popupElements)
	{
		popupElement.classList.remove("open");
	}
}

//
// Component
//

export function initialisePopupOpenElement(popupOpenElement: HTMLElement)
{
	const popupName = BrowserUtilities.ElementClientLib.getStringDataOrThrow(popupOpenElement, "popup");

	const rawPopupPlacement = BrowserUtilities.ElementClientLib.getStringData(popupOpenElement, "popupPlacement") ?? "bottom-start";

	const popupPlacement = PlacementSchema.parse(rawPopupPlacement);

	const popupElement = document.querySelector(`.component-popup[data-name="${ popupName }"]`) as HTMLElement;

	popupOpenElement.addEventListener("click", 
		() =>
		{
			//
			// Store Current State
			//

			const alreadyOpen = popupElement.classList.contains("open");

			//
			// Close All Popups
			//

			closeAllPopups();

			// Note: If this popup was already open, we don't want to open it again
			if (alreadyOpen)
			{
				return;
			}

			//
			// Open Popup
			//

			popupElement.classList.add("open");

			// Note: Only initialise the popup once
			if (popupElement.dataset["initialised"] == "true")
			{
				return;
			}

			//
			// Initialise Popup
			//

			autoUpdate(popupOpenElement, popupElement,
				async () =>
				{
					const position = await computePosition(popupOpenElement, popupElement,
						{
							placement: popupPlacement,
							middleware:
							[
								flip(),
								shift({ limiter: limitShift() }),
							],
						});
	
					Object.assign(popupElement.style,
						{
							left: position.x + "px",
							top: position.y + "px",
						});
				});

			popupElement.dataset["initialized"] = "true";
		});

	popupOpenElement.classList.add("initialised");
}

export function initialisePopupOpenElements()
{
	const popupOpenElements = document.querySelectorAll<HTMLElement>("[data-popup]:not(.initialised)");

	console.log("[Popup] Initialising " + popupOpenElements.length + " popup open elements...");

	for (const popupOpenElement of popupOpenElements)
	{
		try
		{
			initialisePopupOpenElement(popupOpenElement);
		}
		catch (error)
		{
			console.error("[Popup] Error initialising:", popupOpenElement, error);
		}
	}

	if (document.body.dataset["registeredPopupClickListener"] == "true")
	{
		return;
	}

	document.addEventListener("click", 
		(event) =>
		{
			//
			// Check if we clicked a popup open element OR within a popup
			//
	
			const element = event.target as HTMLElement;
	
			const isWithinPopupOpenElement = element?.closest("[data-popup]") != null;
			const isWithinPopupElement = element?.closest(".component-popup") != null;
			const isWithinButtonElement = element?.closest(".component-button") != null;
	
			if (isWithinPopupOpenElement || (isWithinPopupElement && !isWithinButtonElement))
			{
				return;
			}
	
			if (isWithinButtonElement)
			{
				const buttonElement = element.closest(".component-button") as HTMLElement;
	
				if (buttonElement.dataset["closePopup"] == "false")
				{
					return;
				}
			}
	
			//
			// Close all popups
			//
	
			closeAllPopups();
		});

	document.body.dataset["registeredPopupClickListener"] = "true";
}